import { Trans } from "@lingui/macro"
import CloseIcon from '@mui/icons-material/Close'
import { Dialog, DialogContent, FormLabel, IconButton, Typography } from "@mui/material"
import { setAvaliableOrganizations, setOrganization } from "app/redux/actions/OrganizationActions"
import { askToJoinOrganizationByFlow, getAccountParsedById } from "app/services/sfAuth/sfData/sfAccount"
import { saveContactByFlowParsed } from "app/services/sfAuth/sfData/sfContact"
import { saveUserParsed } from "app/services/sfAuth/sfData/sfUser"
import { useFormikContext } from "formik"
import { useSnackbar } from "notistack"
import { FC, useState } from "react"
import { RootStateOrAny, useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import ProgressSnackbar from "../common-components/CustomSnackbars"
import { TextFieldWithFormik } from "../common-components/with-formik/TextFieldWithFormik"
import { connectToOrganizationFlowResults } from "../grants/organization/JoinOrganization"
import { ResourcesDetailsButton } from "../resources/ResourcesDetailsCard/Button"
import { FirstLoginFlowFormikProps } from "./index"

interface JobDialogProps {
  display: boolean
  setDisplay: (display: boolean) => void
}

export const JobDialog: FC<JobDialogProps> = ({ display, setDisplay }) => {

  const { values, handleChange, handleBlur, setFieldValue, setValues } = useFormikContext<FirstLoginFlowFormikProps>()

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const user = useSelector((state: RootStateOrAny) => state.user)

  const avaliableOrganizations = useSelector(
    (state: RootStateOrAny) => state.avaliableOrganizations
  )

  const [loading, setLoading] = useState(false)

  const history = useHistory()

  const dispatch = useDispatch()

  const onClose = (joinOrganizationIds: string[], isRequestSent: boolean, setCurrent?: boolean) => {

    setValues({
      ...values,
      results: null,
      selectedOrganizationId: null,
      jobTitle: '',
      joinOrganizationIds: joinOrganizationIds || values.joinOrganizationIds,
      displayOrganizationConnectDialog: setCurrent? false: true,
      displayOrganizationCreatedWindow: !!setCurrent,
      isRequestSent: setCurrent? false: isRequestSent
    })
  }

  const handleConnect = () => {
    setLoading(true)
    const progressKey = enqueueSnackbar('', {
      persist: true,
      content: key => ProgressSnackbar(<Trans>Updating info</Trans>)
    })
    askToJoinOrganizationByFlow({
      jobTitle: values.jobTitle,
      userId: user.userId,
      orgId: values.selectedOrganizationId || ''
    }).then(
      flowResult => {
        const connectData =
          connectToOrganizationFlowResults[
            flowResult[0].outputValues.result
          ]
        let joinOrganizationIds = values.joinOrganizationIds || []
        if (
          flowResult[0].outputValues.result ===
          'RESULT_REQUEST_SENT'
        ) {
          joinOrganizationIds = [...joinOrganizationIds, values.selectedOrganizationId || '']
        }
        if (connectData) {
          enqueueSnackbar(connectData.messege, {
            variant: connectData.type
          })
        } else {
          enqueueSnackbar(<Trans>Info updated!</Trans>, {
            variant: 'success'
          })
        }
        let saveUserPromise
        if (connectData.setCurrent) {
          saveUserPromise = saveUserParsed({
            id: user.userId,
            associatedOrganizations: values.selectedOrganizationId || ''
          })
        } else {
          saveUserPromise = Promise.resolve()
        }
        if (connectData.noAccountFetch) {
          closeSnackbar(progressKey)
          setFieldValue('results', null)
          onClose(joinOrganizationIds, false, connectData.setCurrent)
          return Promise.resolve()
        }
        return Promise.all([
          saveUserPromise,
          getAccountParsedById(values.selectedOrganizationId || '', {
            permissions: { TEAM_MEMBERS: true }
          }),
          saveContactByFlowParsed({
            id: user?.userObject?.contactId,
            associatedOrganizations: values.selectedOrganizationId || '',
            whatAreYouLookingFor: values.goal
          })
        ]).then(
          ([userSaved, org, contact]) => {
            closeSnackbar(progressKey)
            const isAvaliable = !connectData.notAvailable
            if (
              isAvaliable &&
              !avaliableOrganizations.some(
                org => org.id === values.selectedOrganizationId
              )
            ) {
              dispatch(
                setAvaliableOrganizations([
                  ...avaliableOrganizations,
                  {
                    id: values.selectedOrganizationId,
                    name: org?.organisationsName || ''
                  }
                ])
              )
            }
            if (isAvaliable) {
              dispatch(setOrganization({ ...org }))
            }
            onClose(joinOrganizationIds, true, connectData.setCurrent)
            setDisplay(false)
            // if (connectData.edit) {
            //   history.push('/grants/organization-details')
            // }
          },
          reject => {
            console.log(reject)
            setLoading(false)
          }
        )
      },
      error => {
        console.log(error)
        setLoading(false)
      }
    )
  }

  return(
    <Dialog 
      open={display} 
      onClose={() => setDisplay(false)}
    >
      <IconButton
        aria-label="close"
        onClick={() => setDisplay(false)}
        size="large"
        sx={(theme) => ({
          position: 'absolute',
          right: 0,
          top: 0,
          color: theme.palette.primary.main,
        })}
      >
        <CloseIcon sx={{ fontSize: 32 }}/>
      </IconButton>
      <DialogContent sx={{ padding: '49px 48px 38px 48px', minWidth: '486px' }}>
        <FormLabel sx={{ textAlign: 'center', fontSize: '24px', lineHeight: '28px', mb: '16px' }}>
          <Trans>FIRST_LOGIN_FLOW_ADD_YOUR_JOB_LABEL</Trans>
        </FormLabel>
        {values.results && <Typography variant="body2" sx={{ fontSize: '16px', mb: '8px' }}>
          {values.results.find((result) => result.Id === values.selectedOrganizationId)?.Name}
        </Typography>}
        <Typography variant="body2" sx={{ fontSize: '14px', mb: '8px', fontWeight: 400 }}>
          <Trans>FIRST_LOGIN_FLOW_YOUR_JOB_TITLE</Trans>
        </Typography>
        <TextFieldWithFormik
          name="jobTitle"
          label={<Trans>FIRST_LOGIN_FLOW_YOUR_JOB_TITLE</Trans>}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.jobTitle}
          disabled={loading}
          required
        />
        <ResourcesDetailsButton
          disabled={!values.jobTitle || loading}
          text={<Trans>FIRST_LOGIN_FLOW_CONNECT_WITH_THIS_ORGANIZATION_BUTTON</Trans>}
          onClick={handleConnect}
          sx={{ width: '100%', mt: '24px', height: '35px' }}
        />
      </DialogContent>
    </Dialog>
  )
}